/**
 * Group detail map — filtered map-snapshot markers (REAL get__devices_map-snapshot).
 */

import { ensureMapLibreLoaded } from "../../ops-map-loader.js";
import { clampOpsMapZoom } from "../../ops-map-limits.js";
import {
  createCommandCenterMap,
  buildGroupZoneGeoJson,
  upsertCommandCenterZones,
  buildCcMarkerElement,
  updateCcMarkerElement,
} from "../map/CommandMap.js";
import { applyLocationPinToMarkerEl } from "../../location-pin-lattice.js";

/** @type {null | (() => void)} */
let groupDetailMapTeardown = null;

export function runGlobalGroupDetailMapTeardownIfAny() {
  if (typeof groupDetailMapTeardown === "function") {
    try {
      groupDetailMapTeardown();
    } catch (_) {}
    groupDetailMapTeardown = null;
  }
}

/**
 * @param {unknown[]} items
 * @param {string} groupKey
 * @param {(s: string) => string} canonicalGroupKey
 */
export function filterMapSnapshotForGroup(items, groupKey, canonicalGroupKey) {
  const gk = canonicalGroupKey(String(groupKey || ""));
  if (!gk) return [];
  const out = [];
  for (const raw of Array.isArray(items) ? items : []) {
    const row = /** @type {Record<string, unknown>} */ (raw);
    const rowKey = canonicalGroupKey(String(row.map_group_route_key || row.notification_group || ""));
    if (rowKey !== gk) continue;
    out.push(row);
  }
  return out;
}

/**
 * @param {import('maplibre-gl').Map} map
 * @param {Record<string, unknown>[]} items
 */
function fitMapToGroupItems(map, items) {
  if (!map || !items.length) return;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  let n = 0;
  for (const row of items) {
    const lat = Number(row.map_lat);
    const lng = Number(row.map_lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    n += 1;
  }
  if (!n) return;
  try {
    if (n === 1) {
      map.easeTo({ center: [minLng, minLat], zoom: clampOpsMapZoom(16), duration: 500 });
      return;
    }
    map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 48, maxZoom: 17, duration: 600 });
  } catch (_) {}
}

/**
 * @param {object} opts
 * @param {HTMLElement|null} opts.container
 * @param {string} opts.groupKey
 * @param {(s: string) => string} opts.canonicalGroupKey
 * @param {() => boolean} opts.isAlive
 * @param {(path: string, o?: object) => Promise<unknown>} opts.api
 * @param {(path: string) => string} opts.buildMapSnapshotPath
 * @param {(el: HTMLElement|null, html: string) => void} opts.setChildMarkup
 * @param {(s: string) => string} opts.escapeHtml
 */
export function createGroupDetailMapController(opts) {
  /** @type {import('maplibre-gl').Map | null} */
  let map = null;
  /** @type {Map<string, import('maplibre-gl').Marker>} */
  const markers = new Map();
  let didFit = false;

  const teardown = () => {
    for (const mk of markers.values()) {
      try {
        mk.remove();
      } catch (_) {}
    }
    markers.clear();
    if (map) {
      try {
        const runtime = typeof window !== "undefined" ? window.CrocGeoLibre : null;
        if (runtime && map.__crocReactRootContainer) {
          runtime.unmountMap(map.__crocReactRootContainer);
        } else {
          map.remove();
        }
      } catch (_) {}
      map = null;
    }
    didFit = false;
  };

  runGlobalGroupDetailMapTeardownIfAny();
  groupDetailMapTeardown = () => {
    teardown();
    groupDetailMapTeardown = null;
  };

  const syncMarkers = (items) => {
    if (!map || !opts.isAlive()) return;
    const maplibregl = typeof window !== "undefined" ? window.maplibregl : null;
    if (!maplibregl) return;
    const seen = new Set();
    for (const row of items) {
      const lat = Number(row.map_lat);
      const lng = Number(row.map_lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const ref = String(row.marker_ref || row.device_id || "").trim() || `${lat},${lng}`;
      seen.add(ref);
      const pulseRaw = String(row.map_pulse || (row.is_online ? "online" : "offline"));
      const pulse = pulseRaw === "alert" || pulseRaw === "online" ? pulseRaw : "offline";
      const label = String(row.label || row.device_id || "").trim() || "—";
      let mk = markers.get(ref);
      if (!mk) {
        const el = buildCcMarkerElement(label, pulse);
        try {
          applyLocationPinToMarkerEl(el, row);
        } catch (_) {}
        mk = new maplibregl.Marker({ element: el, anchor: "center" })
          .setLngLat([lng, lat])
          .addTo(map);
        markers.set(ref, mk);
      } else {
        mk.setLngLat([lng, lat]);
        const el = mk.getElement();
        if (el) {
          updateCcMarkerElement(el, label, pulse);
          try {
            applyLocationPinToMarkerEl(el, row);
          } catch (_) {}
        }
      }
    }
    for (const [ref, mk] of markers.entries()) {
      if (!seen.has(ref)) {
        try {
          mk.remove();
        } catch (_) {}
        markers.delete(ref);
      }
    }
    upsertCommandCenterZones(map, buildGroupZoneGeoJson(items));
    if (!didFit && items.length) {
      fitMapToGroupItems(map, items);
      didFit = true;
    }
  };

  const init = async () => {
    const container = opts.container;
    if (!container || !opts.isAlive()) return;
    try {
      await ensureMapLibreLoaded();
      if (!opts.isAlive()) return;
      const created = await createCommandCenterMap({ container, pitch3d: true });
      map = created.map;
      map.once("load", () => {
        if (!opts.isAlive() || !map) return;
        syncMarkers([]);
      });
    } catch (e) {
      opts.setChildMarkup(
        container,
        `<p class="muted" style="padding:12px">Map failed: ${opts.escapeHtml(String(e && e.message ? e.message : e))}</p>`,
      );
    }
  };

  const refresh = async () => {
    if (!opts.isAlive()) return;
    try {
      const res = await opts.api(opts.buildMapSnapshotPath(), { timeoutMs: 16000, retries: 0 });
      const all = (res && /** @type {{ items?: unknown[] }} */ (res).items) || [];
      const filtered = filterMapSnapshotForGroup(all, opts.groupKey, opts.canonicalGroupKey);
      if (!opts.isAlive()) return;
      syncMarkers(filtered);
    } catch (_) {}
  };

  return { init, refresh, teardown };
}
